import React, { useState, useEffect } from "react";
import { MdClose } from "react-icons/md";
import { MdExpandMore } from "react-icons/md";
import { MdChevronLeft } from "react-icons/md";
import { MdChevronRight } from "react-icons/md";
import { useZoomNotification } from "../../../../../hooks/home/tabs/dashboard/components/useZoomNotification";
import "./Announcements.css";

const PAGE_SIZE = 5;

const Announcements = () => {
  const { notifications, loading, error } = useZoomNotification();
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  // 자동/상세 팝업은 전 탭 NoticeIsland에서 담당

  // 모달 열릴 때 페이지·펼침 초기화
  useEffect(() => {
    if (modalOpen) {
      setCurrentPage(1);
      setExpandedId(null);
    }
  }, [modalOpen]);

  // 날짜 파싱 헬퍼
  const parseDate = (date) => {
    if (!date) return null;
    if (date && typeof date === "object" && date.seconds !== undefined) {
      return new Date(date.seconds * 1000);
    }
    if (date && typeof date.toDate === "function") return date.toDate();
    if (date instanceof Date) return date;
    return new Date(date);
  };

  // 짧은 날짜 (리스트용)
  const formatDate = (date) => {
    const dateObj = parseDate(date);
    if (!dateObj || isNaN(dateObj.getTime())) return "";
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    return `${month}. ${day}.`;
  };

  // 긴 날짜 (모달 상세용)
  const formatDateLong = (date) => {
    const dateObj = parseDate(date);
    if (!dateObj || isNaN(dateObj.getTime())) return "";
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    return `${year}. ${month}. ${day}.`;
  };

  // 원문에 실제 개행이 아닌 문자열 "\n"이 그대로 들어있는 경우 실제 줄바꿈으로 치환
  const normalizeDescription = (text) => {
    if (typeof text !== "string") return text;
    return text.replace(/\\r\\n|\\n/g, "\n");
  };

  // is_show가 true인 공지사항만 필터링
  const visibleNotifications = notifications.filter(
    (notification) => notification.is_show
  );

  const totalPages = Math.max(1, Math.ceil(visibleNotifications.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pageItems = visibleNotifications.slice(startIndex, startIndex + PAGE_SIZE);

  const goToPage = (page) => {
    const next = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(next);
    setExpandedId(null);
  };

  if (loading) {
    return (
      <div className="dashboard-widget-card-harf">
        <div className="dashboard-card-header">
          <h3>공지 사항</h3>
        </div>
        <div>로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-widget-card-harf">
        <div className="dashboard-card-header">
          <h3>공지 사항</h3>
        </div>
        <div>공지사항을 불러올 수 없습니다. {error}</div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-widget-card-harf">
        <div className="dashboard-card-header">
          <h3>공지 사항</h3>
          <button
            type="button"
            className="dashboard-more-link"
            onClick={() => setModalOpen(true)}
          >
            더보기
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 5L13 13M13 5L5 13"
                stroke="#696969"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                transform="rotate(45 9 9)"
              />
            </svg>
          </button>
        </div>
        <ul className="dashboard-announcement-list">
          {visibleNotifications.slice(0, 4).map((item, index) => (
            <li key={item.id || `announcement-${index}`}>
              <span>{item.title}</span>
              <span className="date">{formatDate(item.created_at)}</span>
            </li>
          ))}
        </ul>
      </div>

      {modalOpen && (
        <div
          className="announcements-modal-overlay"
          onClick={() => setModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="announcements-modal-title"
        >
          <div
            className="announcements-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="announcements-modal-header">
              <h2 id="announcements-modal-title" className="announcements-modal-title">
                공지 사항
              </h2>
              <button
                type="button"
                className="announcements-modal-close"
                onClick={() => setModalOpen(false)}
                aria-label="닫기"
              >
                <MdClose size={24} />
              </button>
            </div>
            <div className="announcements-modal-body">
              {visibleNotifications.length === 0 ? (
                <p className="announcements-modal-empty">등록된 공지사항이 없습니다.</p>
              ) : (
                <ul className="announcements-modal-list">
                  {pageItems.map((item, idx) => {
                      const globalIndex = startIndex + idx;
                      const id = item.id || `announcement-full-${globalIndex}`;
                      const isExpanded = expandedId === id;
                      return (
                        <li
                          key={id}
                          className={`announcements-modal-item ${isExpanded ? "expanded" : ""}`}
                        >
                          <button
                            type="button"
                            className="announcements-modal-item-trigger"
                            onClick={() => setExpandedId(isExpanded ? null : id)}
                            aria-expanded={isExpanded}
                            aria-controls={`announcements-panel-${id}`}
                            id={`announcements-trigger-${id}`}
                          >
                            <span className="announcements-modal-item-title">{item.title}</span>
                            <span className="announcements-modal-item-meta">
                              {isExpanded && (
                                <span className="announcements-modal-item-date">
                                  {formatDateLong(item.created_at)}
                                </span>
                              )}
                              <MdExpandMore
                                className="announcements-modal-item-chevron"
                                size={22}
                                aria-hidden
                              />
                            </span>
                          </button>
                          <div
                            id={`announcements-panel-${id}`}
                            role="region"
                            aria-labelledby={`announcements-trigger-${id}`}
                            className="announcements-modal-item-panel"
                          >
                            <div className="announcements-modal-item-panel-inner">
                              {item.image_url && (
                                <div className="announcements-modal-item-image-wrap">
                                  <img
                                    src={item.image_url}
                                    alt=""
                                    className="announcements-modal-item-image"
                                  />
                                </div>
                              )}
                              {item.discription && (
                                <div className="announcements-modal-item-description">
                                  {normalizeDescription(item.discription)}
                                </div>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              )}
            </div>
            {visibleNotifications.length > 0 && totalPages > 1 && (
              <div className="announcements-modal-pagination">
                <button
                  type="button"
                  className="announcements-modal-page-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="이전 페이지"
                >
                  <MdChevronLeft size={20} />
                </button>
                <span className="announcements-modal-page-info">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  className="announcements-modal-page-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  aria-label="다음 페이지"
                >
                  <MdChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Announcements;
